import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, AlertTriangle, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import ContactDialog from "@/components/ContactDialog";
import { useAuth } from "@/context/AuthContext";

const SUGGESTED_QUESTIONS = [
  "What's my portfolio worth today?",
  "How much did I earn in dividends YTD?",
  "Backtest my portfolio",
  "What's SCHD yielding right now?",
];

// Pre-filled call-to-action pills that fire local UI actions instead of
// posting to the chatbot backend. Keeps highest-intent flows one click away.
const SUGGESTED_ACTIONS = [
  { id: "guided-tour", label: "Show me how to Get Started", event: "dp:open-tour" },
  { id: "reset-tour",  label: "Reset the Get Started tour", event: "dp:reset-tour" },
];

const STORAGE_KEY = "dpp_chatbot_session";

/**
 * Floating on-site chatbot. Sits bottom-right on every page. Signed-out
 * visitors get the same experience; signed-in users have their name/email
 * prefilled if they escalate to the Contact form.
 */
export default function ChatbotWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Auto-open the chatbot the FIRST time a visitor hits the site. Persists
  // a "seen" flag so we don't pop again on every reload — that would feel
  // like a nag. Users who close the widget still get it back next visit
  // via the floating button.
  const [open, setOpen] = useState(() => {
    try {
      const seen = localStorage.getItem("dp_chatbot_autoopened");
      return !seen;
    } catch { return false; }
  });
  useEffect(() => {
    if (open) {
      try { localStorage.setItem("dp_chatbot_autoopened", "1"); } catch {}
    }
  }, [open]);
  const [messages, setMessages] = useState([]); // {role: "user"|"bot", content, escalate?, reason?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [escalated, setEscalated] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [transcriptForContact, setTranscriptForContact] = useState([]);
  const listRef = useRef(null);

  const persistSession = useCallback((sid) => {
    setSessionId(sid);
    try { localStorage.setItem(STORAGE_KEY, sid); } catch {}
  }, []);

  useEffect(() => {
    // Auto-scroll to newest turn.
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "bot",
        content: `Hi${user?.name ? ` ${user.name.split(" ")[0]}` : ""}! I'm **Ask DPP — your personal AI Agent**. I can look up your real portfolio value, dividends received, run backtests on your saved scenarios, and pull live quotes. Try "what's my portfolio worth?" or "how much did I earn YTD?"`,
      }]);
    }
  }, [open, messages.length, user?.name]);

  // Resume a thread from /conversations. The Conversations page dispatches
  // `dpp:open-chatbot` with the session_id + prior transcript, we hydrate
  // state and open the widget — the next POST reuses the same session_id so
  // the backend's chatbot_sessions doc keeps growing instead of forking.
  useEffect(() => {
    const handler = (ev) => {
      const detail = ev?.detail || {};
      if (detail.session_id) persistSession(detail.session_id);
      if (Array.isArray(detail.messages) && detail.messages.length) {
        setMessages(
          detail.messages.map((m) => ({
            role: m.role === "assistant" ? "bot" : m.role,
            content: m.content,
            tools_used: m.tools_used || [],
          }))
        );
      }
      setOpen(true);
    };
    window.addEventListener("dpp:open-chatbot", handler);
    return () => window.removeEventListener("dpp:open-chatbot", handler);
  }, [persistSession]);

  const send = async (text) => {
    const clean = (text || input).trim();
    if (!clean || sending) return;
    setMessages((m) => [...m, { role: "user", content: clean }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/chatbot/message", { message: clean, session_id: sessionId });
      const { reply, escalate, escalate_reason, session_id: sid, transcript, tools_used, ctas } = r.data;
      persistSession(sid);
      setMessages((m) => [...m, { role: "bot", content: reply, escalate, reason: escalate_reason, tools_used: tools_used || [], ctas: ctas || [] }]);
      if (escalate) {
        setEscalated(true);
        setTranscriptForContact(transcript || []);
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "bot", content: "I can't reach my brain right now — try Contact us and we'll help you directly.", escalate: true, reason: "network" }]);
      setEscalated(true);
    } finally { setSending(false); }
  };

  const openEscalation = () => {
    // Freeze the transcript so a race with a follow-up send doesn't lose it.
    setTranscriptForContact(
      messages.map((m) => ({ role: m.role === "bot" ? "assistant" : "user", content: m.content }))
    );
    setContactOpen(true);
  };

  const reset = () => {
    setMessages([]);
    setEscalated(false);
    setTranscriptForContact([]);
    setSessionId(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    toast.success("Chat reset");
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chatbot-launcher"
          aria-label="Open Ask DPP — your personal AI Agent"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg ring-1 ring-primary/30 grid place-items-center hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-40 w-[min(380px,calc(100vw-1.5rem))] max-h-[min(600px,calc(100vh-2rem))] rounded-xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <div className="flex flex-col leading-tight">
                <span className="serif text-sm font-medium">Ask DPP · Your personal AI Agent</span>
                <span className="mono text-[9px] uppercase tracking-widest opacity-70">Grounded in your real data · Not investment advice</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chatbot-close"
              className="p-1 rounded hover:bg-white/10 transition-colors"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-background/50"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${m.role}-${i}`}
                className={`max-w-[85%] ${
                  m.role === "user"
                    ? "ml-auto"
                    : "mr-auto"
                }`}
              >
                <div
                  className={`rounded-lg px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground border border-border/60"
                  }`}
                >
                  {m.content}
                </div>
                {/* Tool-used chips — show when the agent invoked backend tools
                    to answer this turn, so the user sees the answer was
                    grounded in real DPP data rather than LLM hallucination. */}
                {m.role === "bot" && Array.isArray(m.tools_used) && m.tools_used.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`chatbot-tools-${i}`}>
                    {m.tools_used.map((t, j) => (
                      <span
                        key={j}
                        className={`inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 ${
                          t.ok === false
                            ? "bg-destructive/10 text-destructive"
                            : "bg-primary/10 text-primary"
                        }`}
                        title={`Agent called ${t.name}${t.args && Object.keys(t.args).length ? ` with ${JSON.stringify(t.args)}` : ""}`}
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {t.name.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
                {/* Agent action buttons — rendered under tool-grounded replies
                    so the user can jump straight into the relevant page
                    instead of hunting for it in the nav. */}
                {m.role === "bot" && Array.isArray(m.ctas) && m.ctas.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chatbot-ctas-${i}`}>
                    {m.ctas.slice(0, 4).map((c, j) => (
                      <a
                        key={j}
                        href={c.href}
                        onClick={(e) => {
                          // Keep SPA routing — defer to the host router instead
                          // of a hard reload so the chatbot panel stays mounted.
                          if (c.href?.startsWith("/")) {
                            e.preventDefault();
                            setOpen(false);
                            navigate(c.href);
                          }
                        }}
                        className={`inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 border transition-colors ${
                          c.variant === "primary"
                            ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                            : "bg-card text-foreground border-border hover:bg-muted"
                        }`}
                        data-testid={`chatbot-cta-${i}-${j}`}
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="mr-auto max-w-[85%] rounded-lg px-3 py-2 text-sm bg-muted border border-border/60 text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
              </div>
            )}
            {escalated && !sending && (
              <div className="mt-2 rounded-md border border-secondary/40 bg-secondary/5 p-3" data-testid="chatbot-escalate-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                  <div className="text-xs text-foreground leading-relaxed flex-1">
                    Looks like a human can help you faster. I&apos;ll pass along our chat so you don&apos;t have to retype it.
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={openEscalation}
                  className="w-full mt-2 rounded-full"
                  data-testid="chatbot-escalate-btn"
                >
                  Contact support with transcript
                </Button>
              </div>
            )}
            {messages.length <= 1 && !sending && (
              <div className="pt-2 space-y-1.5" data-testid="chatbot-suggestions">
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Popular questions</div>
                {SUGGESTED_ACTIONS.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      try { window.dispatchEvent(new CustomEvent(a.event)); } catch {}
                      setOpen(false);
                    }}
                    className="block w-full text-left text-xs px-2.5 py-1.5 rounded-md border border-secondary/50 bg-secondary/5 hover:bg-secondary/10 hover:border-secondary transition-colors inline-flex items-center gap-1.5"
                    data-testid={`chatbot-action-${a.id}`}
                  >
                    <Sparkles className="w-3 h-3 text-secondary" /> {a.label}
                  </button>
                ))}
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => send(q)}
                    className="block w-full text-left text-xs px-2.5 py-1.5 rounded-md border border-border hover:border-primary/40 hover:bg-muted/40 transition-colors"
                    data-testid={`chatbot-suggestion-${q.slice(0, 12)}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-border p-2 bg-card flex items-center gap-1.5"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your portfolio…"
              maxLength={800}
              disabled={sending}
              data-testid="chatbot-input"
              className="flex-1 h-9 rounded-full border border-border bg-background px-3 text-sm focus:outline-none focus:border-primary/60"
            />
            <Button
              type="submit"
              size="sm"
              disabled={sending || !input.trim()}
              className="rounded-full h-9 px-3"
              data-testid="chatbot-send"
              aria-label="Send message"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </form>
          <div className="border-t border-border/60 px-3 py-1.5 text-[10px] text-muted-foreground flex items-center justify-between">
            <span className="mono">Session: {sessionId ? sessionId.slice(0, 8) : "new"}</span>
            <button
              type="button"
              onClick={reset}
              className="mono uppercase tracking-widest hover:text-primary transition-colors"
              data-testid="chatbot-reset"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        defaultName={user?.name || ""}
        defaultEmail={user?.email || ""}
        chatTranscript={transcriptForContact}
        onSubmitted={() => {
          setEscalated(false);
          setContactOpen(false);
          toast.success("Message sent — we&apos;ll be in touch soon");
        }}
      />
    </>
  );
}
